import { useMemo, useState } from 'react'
import { useQuery } from 'react-query'
import { CiSearch } from 'react-icons/ci'
import { FiCheck } from 'react-icons/fi'
import DotsLoading from 'components/loadings/dotsLoading'
import { getLoanCandidates } from 'services/fund'
import { useScoreFrom } from 'components/memberScore'
import {
    MAX_GUARANTORS,
    IGuarantorOption,
} from 'components/modals/guarantorPicker'
import { faPlain, toman } from 'utilities/helper/format'

/**
 * «چه کسی پشتش می‌ایستد؟» (optional) — the member list that
 * `GuarantorSection` opens on the terms step. It was step 3 of five until
 * pwa#128 folded it in.
 *
 * A guarantor is not judged the way a borrower is. The question is not «has
 * this person earned a loan» but «if the borrower stops paying, is this
 * person good for it, and are they already standing behind too much?» So the
 * same member list is re-cut around three different facts:
 *
 *  - **سرمایه در صندوق** — what they actually have in the fund, which is what
 *    the box can realistically lean on.
 *  - **ضمانت‌های فعلی** — what they are already on the hook for. A fund whose
 *    guarantees all point at one person has concentrated its risk into one
 *    person, and that is invisible unless it is shown here.
 *  - **وضعیت خودشان** — someone with their own overdue instalments is a weak
 *    guarantee, and the manager should see that before, not after.
 *
 * The borrower's ضامن عضویت is pinned first: funds record it at join time and
 * it is overwhelmingly the same person who ends up backing the loan.
 */
export default function StepGuarantor({
    moneypoolId,
    borrowerPoolshipId,
    borrowerName,
    suggested,
    value,
    onChange,
}: {
    moneypoolId?: string
    borrowerPoolshipId?: number
    borrowerName?: string
    suggested?: IGuarantorOption | null
    value: IGuarantorOption[]
    onChange: (next: IGuarantorOption[]) => void
}) {
    const [search, setSearch] = useState('')

    // the same window step 1 ranked the borrower by
    const [scoreFrom] = useScoreFrom(moneypoolId)
    const { data, isLoading } = useQuery(
        ['loan-candidates', moneypoolId, false, false, scoreFrom],
        getLoanCandidates,
        { enabled: !!moneypoolId, staleTime: 60_000 }
    )

    const rows = useMemo(() => {
        const results = (data?.results ?? []).filter(
            (row) => row.poolship_id !== borrowerPoolshipId
        )
        const term = search.trim()
        const filtered = term
            ? results.filter((row) => row.member_name.includes(term))
            : results
        if (!suggested) return filtered
        // pinned first, and never twice
        const pinned = filtered.find(
            (row) => row.poolship_id === suggested.poolship_id
        )
        if (!pinned) return filtered
        return [pinned, ...filtered.filter((row) => row !== pinned)]
    }, [data, search, borrowerPoolshipId, suggested])

    const isPicked = (poolshipId: number) =>
        value.some((item) => item.poolship_id === poolshipId)

    const toggle = (candidate: ILoanCandidate) => {
        if (isPicked(candidate.poolship_id)) {
            onChange(
                value.filter(
                    (item) => item.poolship_id !== candidate.poolship_id
                )
            )
            return
        }
        if (value.length >= MAX_GUARANTORS) return
        onChange([
            ...value,
            {
                poolship_id: candidate.poolship_id,
                name: candidate.member_name,
            },
        ])
    }

    if (isLoading && !data) {
        return (
            <div className=" py-10">
                <DotsLoading />
            </div>
        )
    }

    return (
        <>
            <div className=" rounded-2xl border border-stroke bg-white p-3">
                <div className=" flex items-center gap-2 rounded-xl bg-canvas px-3 py-2">
                    <CiSearch size={18} className=" shrink-0 text-text-muted" />
                    <input
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                        placeholder="جستجوی نام ضامن"
                        aria-label="جستجوی نام ضامن"
                        className=" w-full bg-transparent text-xs outline-none placeholder:text-text-muted"
                    />
                </div>
                <p className=" mt-2 text-[11px] leading-5 text-text-secondary">
                    ضامن اختیاری است. تا {faPlain.format(MAX_GUARANTORS)} نفر
                    می‌توانید انتخاب کنید
                    {borrowerName
                        ? ` — خودِ ${borrowerName} در فهرست نیست.`
                        : '.'}
                </p>
            </div>

            <div className=" mt-2 space-y-2">
                {rows.map((candidate) => {
                    const picked = isPicked(candidate.poolship_id)
                    const isSuggested =
                        suggested?.poolship_id === candidate.poolship_id
                    const disabled = !picked && value.length >= MAX_GUARANTORS
                    return (
                        <div
                            key={candidate.poolship_id}
                            role="checkbox"
                            aria-checked={picked}
                            aria-disabled={disabled}
                            tabIndex={0}
                            onClick={() => !disabled && toggle(candidate)}
                            onKeyDown={(event) => {
                                if (
                                    !disabled &&
                                    (event.key === 'Enter' || event.key === ' ')
                                ) {
                                    event.preventDefault()
                                    toggle(candidate)
                                }
                            }}
                            /* Selection has to survive a glance on a phone, so
                               it is carried by four things at once: a filled
                               tick, a brand-tinted surface, a thicker border
                               and a right edge bar. A drop shadow alone was
                               reported as not reading as «selected» at all —
                               and it is the one cue that disappears entirely
                               against a light background. */
                            className={` relative w-full overflow-hidden rounded-2xl border p-3 text-right transition-colors ${
                                picked
                                    ? ' border-2 border-brand bg-brand-soft'
                                    : disabled
                                    ? ' border-divider bg-white opacity-50'
                                    : ' cursor-pointer border-stroke bg-white active:bg-canvas'
                            }`}
                        >
                            {picked && (
                                <span
                                    aria-hidden
                                    className=" absolute inset-y-0 right-0 w-1 bg-brand"
                                />
                            )}
                            <div className=" flex items-center justify-between gap-2">
                                <p className=" flex min-w-0 items-center gap-2">
                                    <span
                                        aria-hidden
                                        className={` flex h-5 w-5 shrink-0 items-center justify-center rounded-full border transition-colors ${
                                            picked
                                                ? ' border-brand bg-brand text-white'
                                                : ' border-stroke bg-white'
                                        }`}
                                    >
                                        {picked && <FiCheck size={13} />}
                                    </span>
                                    <span className=" min-w-0 break-words text-sm font-bold text-heading">
                                        {candidate.member_name}
                                    </span>
                                </p>
                                {isSuggested && (
                                    <span className=" shrink-0 rounded-full bg-white px-2 py-0.5 text-[10px] font-bold text-brand-deep">
                                        ضامن عضویت
                                    </span>
                                )}
                            </div>
                            <p className=" mt-1 text-[11px] text-text-secondary">
                                سرمایه {toman(candidate.capital)}
                            </p>
                            {candidate.guaranteed_count > 0 && (
                                <p className=" mt-0.5 text-[11px] text-text-secondary">
                                    الان ضامن{' '}
                                    {faPlain.format(candidate.guaranteed_count)}{' '}
                                    وام است —{' '}
                                    {toman(candidate.guaranteed_outstanding)}{' '}
                                    مانده
                                </p>
                            )}
                            {candidate.overdue_count > 0 ? (
                                <p className=" mt-1.5 rounded-lg bg-danger-soft px-2 py-1 text-[11px] font-bold text-danger-on">
                                    خودش{' '}
                                    {faPlain.format(candidate.overdue_count)}{' '}
                                    قسط معوق دارد
                                </p>
                            ) : candidate.late_installments > 0 ? (
                                <p className=" mt-1.5 rounded-lg bg-warning-soft px-2 py-1 text-[11px] text-warning-on">
                                    قسط معوقی ندارد، ولی{' '}
                                    {faPlain.format(
                                        candidate.late_installments
                                    )}{' '}
                                    قسط را با تاخیر پرداخت کرده
                                </p>
                            ) : null}
                        </div>
                    )
                })}
            </div>
        </>
    )
}
